import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import editorange from "../assets/images/icon/edit-orange.png";
import Input from "components/Input";
import Select from "components/Select";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { updateDepartment } from "services/departments";
import { getWorkplaces } from "services/workplaces";

const buildForm = (dept) => ({
    name: dept?.name || '',
    workplaceId: dept?.workplaceId || '',
    active: dept?.active ?? true,
});

function DepartmentDetailsModal({ department, open, onClose, defaultEditing = false }) {
    const [isEditing, setIsEditing] = useState(defaultEditing);
    const [form, setForm] = useState(buildForm(department));
    const [current, setCurrent] = useState(department);
    const queryClient = useQueryClient();

    const { data: workplacesRaw = [] } = useQuery({ queryKey: ['workplaces'], queryFn: getWorkplaces, enabled: open });
    const workplaceOptions = workplacesRaw.map(w => ({ value: w.id, label: w.name }));
    const workplaceMap = Object.fromEntries(workplacesRaw.map(w => [w.id, w.name]));

    useEffect(() => {
        if (department) {
            setCurrent(department);
            setForm(buildForm(department));
            setIsEditing(defaultEditing);
        }
    }, [department, defaultEditing]);

    const mutation = useMutation({
        mutationFn: updateDepartment,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['departments'] });
            setCurrent(prev => ({ ...prev, name: form.name, workplaceId: form.workplaceId, active: form.active }));
            setIsEditing(false);
        },
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = (e) => {
        e.preventDefault();
        mutation.mutate({
            departmentId: current.id,
            name: form.name,
            workplaceId: form.workplaceId || undefined,
            active: current.active,
        });
    };

    const handleClose = () => { mutation.reset(); onClose(); };

    if (!current) return null;

    return (
        <Modal className='add-workplace-modal' show={open} onHide={handleClose} centered>
            <div className="modal-header border-0 pb-0 d-flex justify-content-between align-items-center">
                <h4 className="sub-title mb-0">Department Detail</h4>
                <button type="button" className="close bg-transparent border-0" onClick={handleClose}>
                    <span aria-hidden="true">&times;</span>
                </button>
            </div>
            <Modal.Body>
                {!isEditing ? (
                    <>
                        <div className='d-flex justify-content-between align-items-center'>
                            <div>
                                <p className='gray-title my-2'>Department Id</p>
                                <h6 className='id-title fw-medium text-black m-0'>{current.id}</h6>
                            </div>
                            <img className='cursor-pointer' src={editorange} alt="edit" onClick={() => setIsEditing(true)} />
                        </div>
                        <div className='border-bottom my-3 pb-4'>
                            <h2 className='sub-title'>General Information</h2>
                            <div className='d-flex workplace-detail-section mt-4'>
                                <div>
                                    <p className='gray-title my-2'>Name</p>
                                    <h6 className='id-title fw-medium text-black m-0'>{current.name || '—'}</h6>
                                </div>
                                <div>
                                    <p className='gray-title my-2'>Workplace</p>
                                    <h6 className='id-title fw-medium text-black m-0'>{workplaceMap[current.workplaceId] || current.workplaceId || '—'}</h6>
                                </div>
                                <div>
                                    <p className='gray-title my-2'>Active Status</p>
                                    <button className='active-button py-1 px-4'>{current.active ? 'Active' : 'Inactive'}</button>
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <form onSubmit={handleSave}>
                        <div className='row mt-3'>
                            <div className='col-12'>
                                <Input label="Department Name" fullWidth variant='flat' name='name' value={form.name} onChange={handleChange} />
                            </div>
                            <div className='col-12 mt-3'>
                                <Select label="Workplace" fullWidth placeholder='Select Workplace' options={workplaceOptions} name='workplaceId' value={form.workplaceId} onChange={handleChange} />
                            </div>
                        </div>
                        {mutation.isError && <p className="text-danger mt-3">Failed to update department. Please try again.</p>}
                        <div className='d-flex gap-2 mt-4'>
                            <button type='submit' className='button-orange py-2 px-4 rounded-pill' disabled={mutation.isPending}>
                                {mutation.isPending ? 'Saving...' : 'Save Changes'}
                            </button>
                            <button type='button' className='btn btn-outline-secondary rounded-pill py-2 px-4' onClick={() => setIsEditing(false)} disabled={mutation.isPending}>
                                Cancel
                            </button>
                        </div>
                    </form>
                )}
            </Modal.Body>
        </Modal>
    );
}

export default DepartmentDetailsModal;
